import React, { useEffect, useState } from "react";
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Strikethrough,
    List,
    ListOrdered,
    Link2,
    Smile,
    Trash2,
    ChevronDown,
    ChevronUp,
    Info,
    Type,
} from "lucide-react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const MAX_BYTES = 1024 * 1024; // 1 MB

function ToolbarButton({ onClick, label, children, disabled }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={label}
            aria-label={label}
            disabled={disabled}
            className="p-1.5 rounded text-stone-600 hover:bg-stone-100 hover:text-orchid-700 disabled:opacity-40"
        >
            {children}
        </button>
    );
}

function formatBytes(n) {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

export default function Snippets() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(true); // editor card open by default
    const [editingId, setEditingId] = useState(null);
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () =>
        adminApi.get("/api/admin/snippets").then((r) => setItems(r.data.items));

    useEffect(() => {
        load();
    }, []);

    const reset = () => {
        setEditingId(null);
        setTitle("");
        setContent("");
    };

    const save = async () => {
        if (!content.trim()) {
            toast.error("Snippet content is required");
            return;
        }
        setSaving(true);
        try {
            if (editingId) {
                await adminApi.patch(`/api/admin/snippets/${editingId}`, { title, content });
                toast.success("Snippet updated");
            } else {
                await adminApi.post("/api/admin/snippets", { title, content });
                toast.success("Snippet added — Priya will train on it");
            }
            reset();
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (s) => {
        setEditingId(s.id);
        setTitle(s.title || "");
        setContent(s.content || "");
        setOpen(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this snippet?")) return;
        await adminApi.delete(`/api/admin/snippets/${id}`);
        toast.success("Deleted");
        load();
    };

    // Toolbar formatting helpers — wrap selection in markdown markers
    const wrapSelection = (left, right = left) => {
        const ta = document.getElementById("snippet-content-area");
        if (!ta) return;
        const start = ta.selectionStart;
        const end = ta.selectionEnd;
        const before = content.slice(0, start);
        const sel = content.slice(start, end);
        const after = content.slice(end);
        const next = `${before}${left}${sel || "text"}${right}${after}`;
        setContent(next);
        setTimeout(() => {
            ta.focus();
            ta.setSelectionRange(start + left.length, start + left.length + (sel || "text").length);
        }, 0);
    };
    const insertAtCursor = (text) => {
        const ta = document.getElementById("snippet-content-area");
        if (!ta) return;
        const start = ta.selectionStart;
        const before = content.slice(0, start);
        const after = content.slice(start);
        setContent(`${before}${text}${after}`);
        setTimeout(() => {
            ta.focus();
            ta.setSelectionRange(start + text.length, start + text.length);
        }, 0);
    };

    const bytes = new Blob([content]).size;
    const overLimit = bytes > MAX_BYTES;

    return (
        <div className="p-10 max-w-5xl" data-testid="page-snippets">
            <div className="mb-8 flex items-start justify-between gap-4">
                <div>
                    <h1 className="font-heading text-4xl text-stone-900">Text snippets</h1>
                    <p className="text-stone-600 mt-2 max-w-xl text-sm">
                        Add text snippets to your knowledge base that the agent gets trained on.
                    </p>
                </div>
                <button
                    className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50"
                    data-testid="snippet-help"
                    onClick={() =>
                        toast.info(
                            "Snippets are short, free-form knowledge entries Priya draws on. Use them for policies, FAQs, location facts, anything Priya should always know."
                        )
                    }
                >
                    <Info size={13} /> Learn more
                </button>
            </div>

            {/* Editor card */}
            <div className="bg-white border border-stone-200 rounded-xl mb-8 overflow-hidden">
                <button
                    onClick={() => setOpen((o) => !o)}
                    data-testid="snippet-editor-toggle"
                    className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-stone-50"
                >
                    <div className="font-medium text-stone-900">
                        {editingId ? "Edit snippet" : "Add text snippet"}
                    </div>
                    {open ? (
                        <ChevronUp size={18} className="text-stone-400" />
                    ) : (
                        <ChevronDown size={18} className="text-stone-400" />
                    )}
                </button>

                {open && (
                    <div className="px-5 pb-5">
                        <label className="block text-sm text-stone-700 mb-1.5">Title</label>
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Ex: Refund requests"
                            data-testid="snippet-title"
                            className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15 mb-4"
                        />

                        {/* Toolbar */}
                        <div className="border border-stone-200 rounded-md overflow-hidden">
                            <div className="px-2 py-1.5 bg-stone-50 border-b border-stone-200 flex items-center gap-0.5">
                                <ToolbarButton onClick={() => insertAtCursor("# ")} label="Heading">
                                    <Type size={14} />
                                </ToolbarButton>
                                <div className="w-px h-4 bg-stone-300 mx-1" />
                                <ToolbarButton onClick={() => wrapSelection("**")} label="Bold">
                                    <Bold size={14} />
                                </ToolbarButton>
                                <ToolbarButton onClick={() => wrapSelection("*")} label="Italic">
                                    <Italic size={14} />
                                </ToolbarButton>
                                <ToolbarButton
                                    onClick={() => wrapSelection("__")}
                                    label="Underline"
                                >
                                    <UnderlineIcon size={14} />
                                </ToolbarButton>
                                <ToolbarButton
                                    onClick={() => wrapSelection("~~")}
                                    label="Strikethrough"
                                >
                                    <Strikethrough size={14} />
                                </ToolbarButton>
                                <div className="w-px h-4 bg-stone-300 mx-1" />
                                <ToolbarButton
                                    onClick={() => insertAtCursor("\n1. ")}
                                    label="Numbered list"
                                >
                                    <ListOrdered size={14} />
                                </ToolbarButton>
                                <ToolbarButton
                                    onClick={() => insertAtCursor("\n- ")}
                                    label="Bulleted list"
                                >
                                    <List size={14} />
                                </ToolbarButton>
                                <div className="w-px h-4 bg-stone-300 mx-1" />
                                <ToolbarButton
                                    onClick={() => {
                                        const url = window.prompt("Enter URL");
                                        if (url) wrapSelection("[", `](${url})`);
                                    }}
                                    label="Link"
                                >
                                    <Link2 size={14} />
                                </ToolbarButton>
                                <ToolbarButton
                                    onClick={() => insertAtCursor("😊")}
                                    label="Emoji"
                                >
                                    <Smile size={14} />
                                </ToolbarButton>

                                <div className="ml-auto text-[11px] text-stone-500" data-testid="snippet-size">
                                    {formatBytes(bytes)} / 1.0 MB
                                </div>
                            </div>
                            <textarea
                                id="snippet-content-area"
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="Enter your text"
                                data-testid="snippet-content"
                                rows={14}
                                className="w-full px-3 py-3 text-sm focus:outline-none font-mono leading-relaxed bg-white"
                            />
                        </div>
                        {overLimit && (
                            <div className="text-xs text-red-700 mt-2">
                                This snippet exceeds the 1 MB limit. Consider splitting it.
                            </div>
                        )}

                        <div className="flex justify-end gap-2 mt-4">
                            {editingId && (
                                <button
                                    onClick={reset}
                                    className="text-sm px-4 py-2 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                onClick={save}
                                disabled={saving || !content.trim() || overLimit}
                                data-testid="snippet-save"
                                className="text-sm font-medium px-5 py-2 rounded-md bg-stone-900 hover:bg-stone-800 text-white disabled:opacity-50"
                            >
                                {saving ? "Saving…" : editingId ? "Update snippet" : "Add text snippet"}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Existing snippets */}
            <div className="space-y-3" data-testid="snippets-list">
                {items.map((s) => (
                    <div key={s.id} className="bg-white border border-stone-200 rounded-lg p-5">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="font-medium text-orchid-700">{s.title || "Untitled"}</div>
                                <div className="text-sm text-stone-700 mt-2 line-clamp-3 whitespace-pre-wrap font-mono">
                                    {s.content}
                                </div>
                                <div className="text-xs text-stone-500 mt-2">
                                    {formatBytes(s.size_bytes || 0)}
                                </div>
                            </div>
                            <div className="flex gap-2 shrink-0">
                                <button
                                    onClick={() => startEdit(s)}
                                    className="text-xs text-orchid-700 hover:underline"
                                    data-testid={`snippet-edit-${s.id}`}
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => remove(s.id)}
                                    className="p-1.5 text-stone-400 hover:text-red-700 hover:bg-red-50 rounded"
                                    data-testid={`snippet-delete-${s.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="bg-white border border-stone-200 rounded-lg p-12 text-center text-stone-500">
                        No snippets yet. Use the editor above to add one.
                    </div>
                )}
            </div>
        </div>
    );
}
